import React, { useState } from 'react';
import { toast } from 'react-toastify';
import visaLogo from '../assets/logo_visa.png';
import mastercardLogo from '../assets/logo_mastercard.png';
import paypalLogo from '../assets/logo_paypal.png';
import applePayLogo from '../assets/logo_apple-pay.png';
import googlePayLogo from '../assets/logo_gpay.png';

import gmcLogo from '../assets/logo_registered_doctors.png';
import hiwLogo from '../assets/logo_hiw.png';
import icoLogo from '../assets/logo_ico.png';
import isoLogo from '../assets/logo_iso_27001.jpg';

import {
    FaFacebookF,
    FaTwitter,
    FaInstagram,
    FaYoutube,
    FaLinkedinIn,
} from 'react-icons/fa';
import { useServiceCatalog } from './ServiceCatalogContext';
import {
    FooterContainer,
    FooterWrapper,
    TopSection,
    NewsletterSection,
    NewsletterForm,
    NewsletterInput,
    NewsletterButton,
    LinksGrid,
    LinkColumn,
    ColumnTitle,
    FooterLinkList,
    FooterLinkItem,
    FooterLink,
    MiddleSection,
    SocialsSection,
    SocialIcons,
    PaymentsSection,
    RegulatorySection,
    TrustpilotWidgetWrapper,
    BottomBar,
    Copyright,
    BottomLinks,
} from './Footer.styles';

// IMPORTANT: Replace these placeholder image URLs with your actual assets
const paymentIcons = [
    { src: visaLogo, alt: 'Visa' },
    { src: mastercardLogo, alt: 'Mastercard' },
    { src: paypalLogo, alt: 'PayPal' },
    { src: applePayLogo, alt: 'Apple Pay' },
    { src: googlePayLogo, alt: 'Google Pay' },
];

const regulatoryLogos = [
    { src: gmcLogo, alt: 'General Medical Council' },
    { src: hiwLogo, alt: 'Health Inspectorate Wales' },
    { src: icoLogo, alt: 'Information Commissioner\'s Office' },
    { src: isoLogo, alt: 'ISO 27001 Certified' },
];

const Footer = () => {
    const currentYear = new Date().getFullYear();
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const { serviceCategories } = useServiceCatalog();

    const handleSubscribe = async (e) => {
        e.preventDefault();
        if (!email) return;

        setLoading(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/newsletter/subscribe`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email }),
            });
            const data = await response.json();
            if (data.success) {
                toast.success(data.message);
                setEmail('');
            } else {
                toast.error(data.error || 'Failed to subscribe.');
            }
        } catch (error) {
            console.error('Newsletter error:', error);
            toast.error('An error occurred. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <FooterContainer>
            <FooterWrapper>
                <TopSection>
                    <NewsletterSection>
                        <h3>Sign up to our newsletter</h3>
                        <p>
                            Get the latest health tips, news, and our latest offers and promotions straight to your inbox.
                        </p>
                        <NewsletterForm onSubmit={handleSubscribe}>
                            <NewsletterInput
                                type="email"
                                placeholder="Enter your email address"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                            />
                            <NewsletterButton type="submit" disabled={loading}>{loading ? '...' : 'Subscribe'}</NewsletterButton>
                        </NewsletterForm>
                    </NewsletterSection>
                    <LinksGrid>
                        <LinkColumn>
                            <ColumnTitle>About Us</ColumnTitle>
                            <FooterLinkList>
                                <FooterLinkItem><FooterLink to="/about">About Us</FooterLink></FooterLinkItem>

                                <FooterLinkItem><FooterLink to="/reviews">Reviews</FooterLink></FooterLinkItem>
                            </FooterLinkList>
                        </LinkColumn>
                        <LinkColumn>
                            <ColumnTitle>Our Services</ColumnTitle>
                            <FooterLinkList>
                                {serviceCategories.map(category => (
                                    <FooterLinkItem key={category.id}>
                                        <FooterLink to={category.path}>{category.name}</FooterLink>
                                    </FooterLinkItem>
                                ))}
                            </FooterLinkList>
                        </LinkColumn>
                        <LinkColumn>
                            <ColumnTitle>Support</ColumnTitle>
                            <FooterLinkList>
                                <FooterLinkItem><FooterLink to="/contact">Contact Us</FooterLink></FooterLinkItem>
                                <FooterLinkItem><FooterLink to="/faq">FAQs</FooterLink></FooterLinkItem>
                                <FooterLinkItem><FooterLink to="/code-of-practice">Code of Practice</FooterLink></FooterLinkItem>
                            </FooterLinkList>
                        </LinkColumn>

                    </LinksGrid>
                </TopSection>

                <MiddleSection>
                    <SocialsSection>
                        <h4>Follow us</h4>
                        <SocialIcons>
                            <a href="https://facebook.com" target="_blank" rel="noopener noreferrer"><FaFacebookF /></a>
                            <a href="https://twitter.com" target="_blank" rel="noopener noreferrer"><FaTwitter /></a>
                            <a href="https://instagram.com" target="_blank" rel="noopener noreferrer"><FaInstagram /></a>
                            <a href="https://youtube.com" target="_blank" rel="noopener noreferrer"><FaYoutube /></a>
                            <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer"><FaLinkedinIn /></a>
                        </SocialIcons>
                    </SocialsSection>
                    <PaymentsSection>
                        {paymentIcons.map((icon, index) => (
                            <img key={index} src={icon.src} alt={icon.alt} />
                        ))}
                    </PaymentsSection>
                </MiddleSection>

                <RegulatorySection>
                    {regulatoryLogos.map((logo, index) => (
                        <img key={index} src={logo.src} alt={logo.alt} />
                    ))}
                </RegulatorySection>

                {/*
                  TrustBox widget - Starter (logo only). Trustpilot blocks all of its
                  star-rating widgets (Mini/Micro/Horizontal/Carousel) until this business
                  has at least one review - swap data-template-id back to
                  "53aa8807dec7e10d38f59f32" (Mini) once reviews start coming in and it
                  will show the live star count and review total automatically.
                */}
                <TrustpilotWidgetWrapper>
                    <div className="trustpilot-widget" data-locale="en-GB" data-template-id="5613c9cde0d31a11924f4650" data-businessunit-id="6a454ceefe9077bc40e20e40" data-style-height="52px" data-style-width="100%">
                        <a href="https://uk.trustpilot.com/review/theletterdoctor.co.uk" target="_blank" rel="noopener noreferrer">Check out our reviews</a>
                    </div>
                </TrustpilotWidgetWrapper>

                <BottomBar>
                    <Copyright>&copy; {currentYear} The Letter Doctor. All rights reserved.</Copyright>
                    <BottomLinks>
                        <FooterLink to="/terms-and-conditions">Terms & Conditions</FooterLink>
                        <FooterLink to="/privacy-policy">Privacy Policy</FooterLink>
                        <FooterLink to="/cookie-policy">Cookie Policy</FooterLink>
                    </BottomLinks>
                </BottomBar>
            </FooterWrapper>
        </FooterContainer>
    );
};

export default Footer;